// Source library. Every factual claim on the site should reference a source id from here.
// External links must always render with target="_blank" + rel="noopener noreferrer".

export type SourceCategory =
  | "CDC: tick surveillance"
  | "CDC: alpha-gal syndrome"
  | "CDC: tickborne diseases"
  | "CDC: tick prevention"
  | "EPA: repellents"
  | "State health departments"
  | "University extensions"
  | "Peer-reviewed research";

export const SOURCE_CATEGORIES: SourceCategory[] = [
  "CDC: tick surveillance",
  "CDC: alpha-gal syndrome",
  "CDC: tickborne diseases",
  "CDC: tick prevention",
  "EPA: repellents",
  "State health departments",
  "University extensions",
  "Peer-reviewed research",
];

export interface Source {
  id: string;
  title: string;
  org: string;
  url: string;
  category: SourceCategory;
  note?: string;
  /** A few words for an inline citation beside a rating, e.g. "CDC MMWR, 2023". */
  short?: string;
}

export const SOURCES: Record<string, Source> = {
  "cdc-tick-surveillance": {
    id: "cdc-tick-surveillance",
    title: "Surveillance and Control of Ticks",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/surveillance/index.html",
    category: "CDC: tick surveillance",
    note: "Background on how tick presence is tracked across the United States.",
  },
  "cdc-tick-geography": {
    id: "cdc-tick-geography",
    title: "Where Ticks Live",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/about/where-ticks-live.html",
    category: "CDC: tick surveillance",
    note: "The Lone Star tick is widely distributed in the Northeast, South, and Midwest U.S.",
  },
  "cdc-lone-star-surveillance": {
    id: "cdc-lone-star-surveillance",
    title: "Lone Star Tick Surveillance",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/data-research/facts-stats/lone-star-tick-surveillance.html",
    category: "CDC: tick surveillance",
    note: "County-level established vs. reported records. 'Established' = 6+ ticks or multiple life stages collected in 12 months.",
  },
  "cdc-lst-county-data": {
    id: "cdc-lst-county-data",
    title: "Lone Star Tick Surveillance Map Data, 2025 (county status)",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/media/files/2026/05/2025-lone-star-tick-surveillance-map-data.xlsx",
    category: "CDC: tick surveillance",
    note: "Every county's status: Established (6 or more ticks, or more than one life stage, found in a year), Reported (fewer), or No records. Linked from CDC's Lone Star Tick Surveillance page.",
    short: "CDC county data, 2025",
  },
  "mmwr-2026-biltmore": {
    id: "mmwr-2026-biltmore",
    title:
      "Southward Expansion of Ixodes scapularis Tick Pathogens — Western North Carolina, 2024–2025 (MMWR)",
    org: "CDC / MMWR",
    url: "https://www.cdc.gov/mmwr/volumes/75/wr/mm7531a1.htm",
    category: "CDC: tick surveillance",
    note: "Tick collection in Biltmore Forest, Buncombe County, found five Lone Star ticks among 373 ticks.",
    short: "CDC MMWR, 2026",
  },
  "cdc-ags": {
    id: "cdc-ags",
    title: "Alpha-gal Syndrome",
    org: "CDC",
    url: "https://www.cdc.gov/alpha-gal-syndrome/about/index.html",
    category: "CDC: alpha-gal syndrome",
    note: "What alpha-gal syndrome is, symptoms, and its association with tick bites.",
  },
  "cdc-ags-diagnosis": {
    id: "cdc-ags-diagnosis",
    title: "Diagnosis and Testing for Alpha-gal Syndrome",
    org: "CDC",
    url: "https://www.cdc.gov/alpha-gal-syndrome/hcp/diagnosis-testing/index.html",
    category: "CDC: alpha-gal syndrome",
    note: "Diagnosis is made by a licensed clinician; testing decisions are clinical.",
  },
  "cdc-ags-distribution": {
    id: "cdc-ags-distribution",
    title: "Geographic Distribution of Suspected Alpha-gal Syndrome Cases — U.S., 2017–2022 (MMWR)",
    org: "CDC / MMWR",
    url: "https://www.cdc.gov/mmwr/volumes/72/wr/mm7230a2.htm",
    category: "CDC: alpha-gal syndrome",
    short: "CDC MMWR, 2023",
    note: "Highest suspected-AGS prevalence across a contiguous southern, midwestern, and mid-Atlantic region; most cases align with the established Lone Star tick range.",
  },
  "cdc-prevent-bites": {
    id: "cdc-prevent-bites",
    title: "Preventing Tick Bites",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/prevention/index.html",
    category: "CDC: tick prevention",
  },
  "cdc-remove-tick": {
    id: "cdc-remove-tick",
    title: "Tick Removal",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/removing-a-tick/index.html",
    category: "CDC: tick prevention",
    note: "Step-by-step guidance for removing an attached tick.",
  },
  "cdc-tickborne": {
    id: "cdc-tickborne",
    title: "Tickborne Diseases of the United States",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/data-research/facts-stats/index.html",
    category: "CDC: tick surveillance",
  },
  "cdc-ehrlichiosis": {
    id: "cdc-ehrlichiosis",
    title: "Ehrlichiosis",
    org: "CDC",
    url: "https://www.cdc.gov/ehrlichiosis/",
    category: "CDC: tickborne diseases",
    note: "Bacterial illness transmitted primarily by the Lone Star tick; treatable with prompt antibiotics.",
  },
  "cdc-heartland": {
    id: "cdc-heartland",
    title: "Heartland Virus",
    org: "CDC",
    url: "https://www.cdc.gov/heartland-virus/",
    category: "CDC: tickborne diseases",
    note: "Rare virus associated with Lone Star tick bites; no specific antiviral treatment.",
  },
  "cdc-bourbon": {
    id: "cdc-bourbon",
    title: "Bourbon Virus",
    org: "CDC",
    url: "https://www.cdc.gov/bourbon-virus/",
    category: "CDC: tickborne diseases",
    note: "Rare tickborne virus first identified in the U.S. in 2014; supportive care only.",
  },
  "cdc-tularemia": {
    id: "cdc-tularemia",
    title: "Tularemia",
    org: "CDC",
    url: "https://www.cdc.gov/tularemia/",
    category: "CDC: tickborne diseases",
    note: "Bacterial illness that can be spread by tick bites (including the Lone Star tick) and other routes.",
  },
  "cdc-stari": {
    id: "cdc-stari",
    title: "Tickborne Diseases of the United States (STARI)",
    org: "CDC",
    url: "https://www.cdc.gov/ticks/data-research/facts-stats/index.html",
    category: "CDC: tickborne diseases",
    note: "Southern Tick-Associated Rash Illness (STARI) follows some Lone Star tick bites; cause not established and it is not Lyme disease.",
  },
  "cdc-lyme": {
    id: "cdc-lyme",
    title: "Lyme Disease",
    org: "CDC",
    url: "https://www.cdc.gov/lyme/",
    category: "CDC: tickborne diseases",
    note: "Spread by blacklegged (deer) ticks, NOT the Lone Star tick. Early sign is often an expanding erythema migrans rash.",
  },
  "cdc-rmsf": {
    id: "cdc-rmsf",
    title: "Rocky Mountain Spotted Fever (RMSF)",
    org: "CDC",
    url: "https://www.cdc.gov/rmsf/",
    category: "CDC: tickborne diseases",
    note: "Spread by American dog, Rocky Mountain wood, and brown dog ticks. Most US cases are in South-Central states; can be fatal without prompt doxycycline.",
  },
  "cdc-anaplasmosis": {
    id: "cdc-anaplasmosis",
    title: "Anaplasmosis",
    org: "CDC",
    url: "https://www.cdc.gov/anaplasmosis/",
    category: "CDC: tickborne diseases",
    note: "Spread by blacklegged and western blacklegged ticks; treatable with prompt antibiotics.",
  },
  "cdc-babesiosis": {
    id: "cdc-babesiosis",
    title: "Babesiosis",
    org: "CDC",
    url: "https://www.cdc.gov/babesiosis/",
    category: "CDC: tickborne diseases",
    note: "A parasitic infection of red blood cells spread mainly by the blacklegged tick, concentrated in the Northeast and upper Midwest.",
  },
  "cdc-powassan": {
    id: "cdc-powassan",
    title: "Powassan Virus",
    org: "CDC",
    url: "https://www.cdc.gov/powassan/",
    category: "CDC: tickborne diseases",
    note: "A rare but serious virus spread by blacklegged ticks; can be transmitted within hours and has no specific treatment.",
  },
  "epa-repellent-search": {
    id: "epa-repellent-search",
    title: "Find the Repellent That Is Right for You",
    org: "EPA",
    url: "https://www.epa.gov/insect-repellents/find-repellent-right-you",
    category: "EPA: repellents",
    note: "Search tool for EPA-registered repellents (DEET, picaridin, oil of lemon eucalyptus).",
  },
  "epa-permethrin": {
    id: "epa-permethrin",
    title: "Repellent-Treated Clothing",
    org: "EPA",
    url: "https://www.epa.gov/insect-repellents/repellent-treated-clothing",
    category: "EPA: repellents",
    note: "Guidance on permethrin-treated clothing and gear.",
  },
  tickencounter: {
    id: "tickencounter",
    title: "TickEncounter Resource Center",
    org: "University of Rhode Island",
    url: "https://web.uri.edu/tickencounter/",
    category: "University extensions",
    note: "Tick identification, prevention, and reporting resources.",
  },
  "uga-extension-ags": {
    id: "uga-extension-ags",
    title: "Lone Star Tick (identification and life stages)",
    org: "Texas A&M AgriLife TickApp",
    url: "https://tickapp.tamu.edu/home/tick-identification/lone-star-tick/",
    category: "University extensions",
    note: "University extension guide to the Lone Star tick's markings, life stages and seasons.",
  },
  "ncbi-ags-review": {
    id: "ncbi-ags-review",
    title: "Diagnosis and Management of Patients with the α-Gal Syndrome",
    org: "Platts-Mills et al., J Allergy Clin Immunol Pract (2020)",
    url: "https://doi.org/10.1016/j.jaip.2019.09.017",
    category: "Peer-reviewed research",
    note: "Peer-reviewed clinical review of how alpha-gal syndrome presents, is diagnosed and is managed.",
  },
  "plos-aa-distribution": {
    id: "plos-aa-distribution",
    title: "Current and Future Distribution of the Lone Star Tick in North America",
    org: "Raghavan et al., PLOS ONE (2019)",
    url: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0209082",
    category: "Peer-reviewed research",
    note: "Ecological niche modeling of Amblyomma americanum range and projected northward expansion.",
  },
  "uky-ef648": {
    id: "uky-ef648",
    title: "Lone Star Tick (Entfact 648)",
    org: "University of Kentucky Entomology",
    url: "https://entomology.mgcafe.uky.edu/ef648",
    category: "University extensions",
    note: "Lone star ticks are active from March through September.",
  },
  "mdc-avoiding-ticks": {
    id: "mdc-avoiding-ticks",
    title: "Avoiding Ticks",
    org: "Missouri Department of Conservation",
    url: "https://mdc.mo.gov/blogs/discover-nature-notes/avoiding-ticks",
    category: "State health departments",
    note: "Ticks are active from April through September.",
  },
  "uf-news-2026-ticks": {
    id: "uf-news-2026-ticks",
    title: "Don't Let a Tick Make You Sick: How to Stay Safe This Summer",
    org: "University of Florida",
    url: "https://news.ufl.edu/2026/06/summer-ticks/",
    category: "University extensions",
    note: "The lone star tick is Florida's most common human-biting tick. Ticks are active year-round; activity peaks between April and August.",
  },
  "rutgers-fs1281": {
    id: "rutgers-fs1281",
    title: "The Lone Star Tick in New Jersey (FS1281)",
    org: "Rutgers NJAES",
    url: "https://njaes.rutgers.edu/fs1281/",
    category: "University extensions",
    note: "In New Jersey, adults are active April to mid-June, nymphs April to July, and larvae July to September.",
  },
  "pa-dep-lst": {
    id: "pa-dep-lst",
    title: "Lone Star Tick",
    org: "Pennsylvania Department of Environmental Protection",
    url: "https://www.pa.gov/agencies/dep/programs-and-services/integration/vector-management/ticks/lone-star-tick",
    category: "State health departments",
    note: "Nymphs are active May through early August; adults April through the end of June.",
  },
  "psu-ext-lst": {
    id: "psu-ext-lst",
    title: "Know Your Vector: Lone Star Ticks",
    org: "Penn State Extension",
    url: "https://extension.psu.edu/know-your-vector-lonestar-ticks",
    category: "University extensions",
    note: "Lone star ticks can now be found across Pennsylvania, but are often patchily distributed.",
  },
  "uri-tickencounter-lst": {
    id: "uri-tickencounter-lst",
    title: "Lone Star Tick",
    org: "University of Rhode Island TickEncounter",
    url: "https://web.uri.edu/tickencounter/species/lone-star-tick/",
    category: "University extensions",
    note: "Adults are active April to late August. Larvae typically latch on by the hundreds. Rhode Island reports have mostly been confined to Prudence Island.",
  },
  "isu-lst": {
    id: "isu-lst",
    title: "Lone Star Tick",
    org: "Iowa State University Extension",
    url: "https://yardandgarden.extension.iastate.edu/encyclopedia/lone-star-tick",
    category: "University extensions",
    note: "Fairly abundant in Iowa in recent years, especially in the southern half; most active in May and June.",
  },
  "idph-common-ticks": {
    id: "idph-common-ticks",
    title: "Common Ticks",
    org: "Illinois Department of Public Health",
    url: "https://dph.illinois.gov/topics-services/environmental-health-protection/structural-pest-control/common-ticks.html",
    category: "State health departments",
    note: "Primarily found in the southern half of Illinois; most active from April through the end of July.",
  },
  "umd-ext-ticks": {
    id: "umd-ext-ticks",
    title: "Ticks in Maryland",
    org: "University of Maryland Extension",
    url: "https://extension.umd.edu/resource/ticks-maryland",
    category: "University extensions",
    note: "Lone star tick adults April to late August; nymphs May to early August; larvae July to late September.",
  },
  "ks-tick-guide": {
    id: "ks-tick-guide",
    title: "Kansas Tick Guide",
    org: "Kansas Department of Health and Environment / K-State",
    url: "https://keap.kdhe.ks.gov/Ephtm/EphtContent/documents/Kansas%20Tick%20Guide%20KState.pdf",
    category: "State health departments",
    note: "Lone star tick: active late February through fall.",
  },
  "ksu-mf2653": {
    id: "ksu-mf2653",
    title: "Ticks in Kansas (MF2653)",
    org: "K-State Research and Extension",
    url: "https://bookstore.ksre.ksu.edu/download/ticks-in-kansas_MF2653",
    category: "University extensions",
    note: "The lone star tick has become common in the eastern half of Kansas.",
  },
  "ga-dph-tbd": {
    id: "ga-dph-tbd",
    title: "Tick-borne Diseases",
    org: "Georgia Department of Public Health",
    url: "https://dph.georgia.gov/epidemiology/zvbd/tbd",
    category: "State health departments",
    note: "Tick-borne illnesses are most often transmitted between early spring and late fall. Alpha-gal syndrome is among emerging tickborne diseases DPH is watching, though cases are rarely reported in Georgia.",
  },
  "uga-gray-2017": {
    id: "uga-gray-2017",
    title: "Summertime Is Tick Time in Georgia",
    org: "University of Georgia CAES",
    url: "https://fieldreport.caes.uga.edu/summertime-is-tick-time-in-georgia-time-to-get-tick-smart/",
    category: "University extensions",
    note: "Georgia's lone star tick is the state's most common tick.",
  },
  "msu-ext-ticks": {
    id: "msu-ext-ticks",
    title: "Species Spotlight: Ticks",
    org: "Mississippi State University Extension",
    url: "https://extension.msstate.edu/blogs/extension-for-real-life/species-spotlight-ticks",
    category: "University extensions",
    note: "Lone star ticks are especially active from early spring through fall.",
  },
  "adph-tick": {
    id: "adph-tick",
    title: "Ticks",
    org: "Alabama Department of Public Health",
    url: "https://alabamapublichealth.gov/tick/index.html",
    category: "State health departments",
    note: "Ticks are most active in early spring through late fall; tick-borne illnesses are reported year-round in Alabama.",
  },
  "ncdhhs-2022": {
    id: "ncdhhs-2022",
    title: "Warmer Weather Leads to Greater Risk of Tick- and Mosquito-borne Illness",
    org: "North Carolina DHHS",
    url: "https://www.ncdhhs.gov/news/press-releases/2022/04/19/warmer-weather-leads-greater-risk-tick-and-mosquito-born",
    category: "State health departments",
    note: "Ticks are most active in spring, summer and early fall in North Carolina.",
  },
  "wv-dhhr-2022": {
    id: "wv-dhhr-2022",
    title: "DHHR Urges Residents to Take Steps to Prevent Tickborne Diseases",
    org: "West Virginia DHHR",
    url: "https://health.wv.gov/article/dhhr-urges-residents-take-steps-prevent-tickborne-diseases",
    category: "State health departments",
    note: "Most infections occur from late spring through early fall, when ticks are most active.",
  },
  "mcclung-2023-ok": {
    id: "mcclung-2023-ok",
    title: "Seasonality of Amblyomma americanum Activity in North Central Oklahoma",
    org: "McClung et al., Vector-Borne and Zoonotic Diseases (2023)",
    url: "https://doi.org/10.1089/vbz.2023.0009",
    category: "Peer-reviewed research",
    note: "Adults were collected mainly March to June, nymphs March to August, larvae July to September.",
  },
  "tamu-tickapp-lst": {
    id: "tamu-tickapp-lst",
    title: "Lone Star Tick",
    org: "Texas A&M AgriLife TickApp",
    url: "https://tickapp.tamu.edu/home/tick-identification/lone-star-tick/",
    category: "University extensions",
    note: "Adults peak March to May; nymphs April to June and July to August; larvae June to August.",
  },
  "saunders-2025-eid-maine": {
    id: "saunders-2025-eid-maine",
    title: "Lone Star Ticks and Alpha-gal Syndrome in Maine",
    org: "Emerging Infectious Diseases (CDC), 2025",
    url: "https://wwwnc.cdc.gov/eid/article/31/4/24-1265_article",
    category: "Peer-reviewed research",
    note: "Lone star ticks have been found sporadically in Maine since the 1990s, but established populations have not been identified.",
  },
  "mdh-alphagal": {
    id: "mdh-alphagal",
    title: "Alpha-gal Syndrome",
    org: "Minnesota Department of Health",
    url: "https://www.health.mn.gov/diseases/alphagal/index.html",
    category: "State health departments",
    note: "AGS is primarily associated with the lone star tick, which is rarely found in Minnesota.",
  },
  "wi-ticks-ags": {
    id: "wi-ticks-ags",
    title: "Alpha-gal Syndrome (Red Meat Allergy)",
    org: "University of Wisconsin Tick Lab",
    url: "https://wisconsin-ticks.russell.wisc.edu/alpha-gal-syndrome-red-meat-allergy/",
    category: "University extensions",
    note: "Wisconsin does not have a widely established population of lone star ticks.",
  },
  "osu-klompen-2017": {
    id: "osu-klompen-2017",
    title: "Lone Star Ticks in Ohio",
    org: "Ohio State University Museum of Biological Diversity",
    url: "https://u.osu.edu/biomuseum/?p=5577",
    category: "University extensions",
    note: "Amblyomma americanum used to be uncommon in southern Ohio, but has increased in numbers and range.",
  },
  "caes-2017-lst": {
    id: "caes-2017-lst",
    title: "Lone Star Tick Population Found in Connecticut",
    org: "Connecticut Agricultural Experiment Station",
    url: "https://portal.ct.gov/-/media/caes/documents/publications/press_releases/2017/caesdeeppressreleaselonestartick",
    category: "State health departments",
    note: "A heavy population of the lone star tick was detected at Manresa Island in South Norwalk.",
  },
  "mmwr-2026-seroprevalence": {
    id: "mmwr-2026-seroprevalence",
    title: "Alpha-gal IgE Seroprevalence Among Blood Donors, 10 States, 2024–2025 (MMWR)",
    org: "CDC / MMWR",
    url: "https://www.cdc.gov/mmwr/volumes/75/wr/mm7525a1.htm",
    category: "CDC: alpha-gal syndrome",
    note: "South Carolina has established lone star tick populations but fewer suspected AGS cases than its neighboring states.",
  },
  "state-health-departments": {
    id: "state-health-departments",
    title: "State & Local Health Departments",
    org: "Directory",
    url: "https://www.cdc.gov/publichealthgateway/healthdirectories/index.html",
    category: "State health departments",
    note: "Find your state or local health department for region-specific guidance.",
  },
};

export function getSources(ids: string[]): Source[] {
  return ids.map((id) => SOURCES[id]).filter((s): s is Source => Boolean(s));
}

export function sourcesByCategory(): Record<SourceCategory, Source[]> {
  const out = {} as Record<SourceCategory, Source[]>;
  for (const cat of SOURCE_CATEGORIES) out[cat] = [];
  for (const s of Object.values(SOURCES)) out[s.category].push(s);
  return out;
}

/** Sensible default source set when a page doesn't specify its own. */
export const DEFAULT_SOURCE_IDS = [
  "cdc-ags",
  "cdc-prevent-bites",
  "cdc-tick-geography",
  "epa-repellent-search",
];
